import React, { useState, useEffect, useMemo } from 'react'
import { t } from 'i18next'
import parsePhoneNumber from 'libphonenumber-js/max'
import { GoogleMap, Autocomplete, Marker } from '@react-google-maps/api'
import MiniLoading from '../MiniLoading'
import no from '../../Assets/Images/Login/no.svg'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronDown } from '@fortawesome/free-solid-svg-icons'
import {
  getBuildingTypes,
  getPhoneTypes,
  UNIT_NUMBER_KEYS,
  UNIT_NUMBER_PLACEHOLDER_KEYS,
  showsFloorAndElevator,
} from 'Components/AddressForm/addressFormConstants'
import { BuildingTypeSelector } from 'Components/AddressForm/BuildingTypeSelector'
import { PhoneTypeSelector } from 'Components/AddressForm/PhoneTypeSelector'
import { DefaultAddressSection } from 'Components/AddressForm/DefaultAddressSection'
import { isValidNumberType } from 'Utils/phoneValidation'
import './mapa.css'
import { useSelector, useDispatch } from 'react-redux'
import { agregarDireccion } from '../../redux/actions/cotizacionActions.js'
import { CircularProgress, Switch } from '@mui/material'
import {
  fetchAddressByGeolocation,
  fetchAddressCountedCustomer,
  fetchDistrictByProvince,
  fetchProvincesByCountryAction,
  fetchTownshipByDistrict,
  fetchUpdateAddress,
} from 'redux/actions/clientsActions'
import {
  buildingTypeIds,
  defaultCountryId,
  telephoneTypeIds,
  DEFAULT_PHONE_PREFIX,
} from 'Utils/constants'
import styles from './mapa.module.css'
import { PhoneNumberField } from 'Views/Clientes/CrearCliente/components/PhoneNumberField/PhoneNumberField'
import { sanitizeText, validatorIsCountedCustomer } from 'Utils/functions'
import { updateAddress } from 'api/mdl03'

// Placeholder sentinels – used both as initial state and in comparisons
const COUNTRY_PH = 'PÁIS'
const PROVINCE_PH = 'PROVINCIA'
const DISTRICT_PH = 'DISTRITO'
const TOWNSHIP_PH = 'CORREGIMIENTO'

function Mapa({
  setSelectedAddress,
  setDireccion,
  handleMetodoEntrega,
  isModalScreen,
  defaultValues,
  onClose,
  showHeader = true,
  customerId,
  setIsErrorAddressModal,
  setMessageAddressModal,
}) {
  const clienteId = useSelector(
    state => state?.cotizacion?.QuoteHeader?.CustomerId
  )

  const mapContainerStyle = { height: '330px', width: '100%' }

  const dispatch = useDispatch()
  const hasDefaultAddress =
    !!defaultValues?.AddressId || !!defaultValues?.Address_AddressId

  // ---------------------------------------------------------------------------
  // Address text fields
  // ---------------------------------------------------------------------------
  const [address, setAddress] = useState({
    alias: hasDefaultAddress
      ? defaultValues?.Name ||
        defaultValues?.Address_Name ||
        defaultValues?.Address_AddressName ||
        ''
      : '',
    neighborhood: hasDefaultAddress
      ? defaultValues?.Street ||
        defaultValues?.NeighborhoodName ||
        defaultValues?.Neighborhood?.Name ||
        ''
      : '',
    unitNumber: hasDefaultAddress
      ? defaultValues?.UnitNumber || defaultValues?.ApartmentOrStoreNumber || ''
      : '',
    floorNumber: hasDefaultAddress
      ? defaultValues?.FloorNumber || defaultValues?.HomeOrFloorNumber || ''
      : '',
    additionalInstructions: hasDefaultAddress
      ? defaultValues?.AdditionalInstructions ||
        defaultValues?.AddressDetail ||
        defaultValues?.Comment ||
        ''
      : '',
  })

  // ---------------------------------------------------------------------------
  // Building type
  // ---------------------------------------------------------------------------
  const [selectedBuildingTypeId, setSelectedBuildingTypeId] = useState(
    hasDefaultAddress
      ? (defaultValues?.BuildingTypeId || buildingTypeIds.casa).toLowerCase()
      : buildingTypeIds.casa
  )
  const [hasElevator, setHasElevator] = useState(
    hasDefaultAddress ? defaultValues?.HasElevator || false : false
  )

  // ---------------------------------------------------------------------------
  // Phone
  // ---------------------------------------------------------------------------
  const defaultPhone = (
    defaultValues?.AddressTelephone?.find(
      t =>
        t.AddressPhoneInfo?.AddressDefaultMobile ||
        t.AddressPhoneInfo?.AddressDefaultHome ||
        t.AddressPhoneInfo?.AddressDefaultWork ||
        t.AddressPhoneInfo?.DefaultMobile ||
        t.AddressPhoneInfo?.DefaultHome ||
        t.AddressPhoneInfo?.DefaultWork
    ) ?? defaultValues?.AddressTelephone?.[0]
  )?.AddressPhoneInfo

  const [telephoneTypeId, setTelephoneTypeId] = useState(
    (hasDefaultAddress &&
      defaultPhone?.AddressTelephoneTypeId?.toLowerCase()) ||
      telephoneTypeIds.cellPhone
  )

  const defaultPrefix =
    (hasDefaultAddress && defaultPhone?.AddressPrefix) || DEFAULT_PHONE_PREFIX

  const defaultPhoneNumber = (() => {
    if (!hasDefaultAddress) return ''
    if (defaultPhone?.AddressNumber) return defaultPhone.AddressNumber
    const raw =
      defaultValues?.PhoneNumber ||
      defaultValues?.Address_Telephone_Number ||
      defaultValues?.Telephone ||
      ''
    return raw.startsWith(defaultPrefix) ? raw.slice(defaultPrefix.length) : raw
  })()

  const [phoneData, setPhoneData] = useState({
    Prefix: defaultPrefix,
    number: defaultPhoneNumber,
  })

  // ---------------------------------------------------------------------------
  // Default address flags
  // ---------------------------------------------------------------------------
  const [isDefaultDelivery, setIsDefaultDelivery] = useState(
    hasDefaultAddress
      ? defaultValues?.IsDefaultDeliveryAddress ||
          defaultValues?.DefaultShippingAddress ||
          false
      : false
  )
  const [isDefaultBilling, setIsDefaultBilling] = useState(
    hasDefaultAddress
      ? defaultValues?.IsDefaultBillingAddress ||
          defaultValues?.DefaultBillingAddress ||
          false
      : false
  )

  // ---------------------------------------------------------------------------
  // Geographic dropdowns
  // ---------------------------------------------------------------------------
  const [countryCode, setCountryCode] = useState('')
  const [selectedCountry, setSelectedCountry] = useState(COUNTRY_PH)
  const [provinces, setProvinces] = useState([])
  const [selectedProvince, setSelectedProvince] = useState(PROVINCE_PH)
  const [provinceCode, setProvinceCode] = useState('')
  const [districts, setDistricts] = useState([])
  const [selectedDistrict, setSelectedDistrict] = useState(DISTRICT_PH)
  const [districtCode, setDistrictCode] = useState('')
  const [townships, setTownships] = useState([])
  const [selectedTownship, setSelectedTownship] = useState(TOWNSHIP_PH)
  const [townshipCode, setTownshipCode] = useState('')

  const [showCountries, setShowCountries] = useState(false)
  const [showProvinces, setShowProvinces] = useState(false)
  const [showDistricts, setShowDistricts] = useState(false)
  const [showTownships, setShowTownships] = useState(false)

  // ---------------------------------------------------------------------------
  // Validation errors
  // ---------------------------------------------------------------------------
  const [errors, setErrors] = useState({
    country: false,
    province: false,
    district: false,
    township: false,
    neighborhood: false,
    unitNumber: false,
    alias: false,
    phoneNumber: false,
  })

  const useSelectorQuoteObject = useSelector(state => state.cotizacion)
  const customerSavedLocations = useSelector(
    state => state.sellOrder.customerInfoObject
  )
  const isCountedCustomer = validatorIsCountedCustomer(
    useSelectorQuoteObject?.QuoteHeader
  )

  // ---------------------------------------------------------------------------
  // Map
  // ---------------------------------------------------------------------------
  const [lat, setLat] = useState(
    parseFloat(window.localStorage.getItem('lat')) || 8.98
  )
  const [lng, setLng] = useState(
    parseFloat(window.localStorage.getItem('long')) || -79.51
  )
  const [autocomplete, setAutocomplete] = useState(null)
  const [formIsLoading, setFormIsLoading] = useState(false)
  const [saveAddressLoadingState, setSaveAddressLoadingState] = useState(false)

  // ---------------------------------------------------------------------------
  // Computed final address
  // ---------------------------------------------------------------------------
  const buildingTypeLabel = useMemo(
    () =>
      getBuildingTypes().find(bt => bt.id === selectedBuildingTypeId)?.label ||
      '',
    [selectedBuildingTypeId]
  )

  const computedFinalAddress = useMemo(() => {
    return [
      buildingTypeLabel,
      address.unitNumber,
      address.neighborhood,
      selectedTownship !== TOWNSHIP_PH ? selectedTownship : '',
      selectedDistrict !== DISTRICT_PH ? selectedDistrict : '',
      selectedProvince !== PROVINCE_PH ? selectedProvince : '',
      selectedCountry !== COUNTRY_PH ? selectedCountry : '',
    ]
      .filter(Boolean)
      .join(', ')
      .substring(0, 99)
  }, [
    buildingTypeLabel,
    address.unitNumber,
    address.neighborhood,
    selectedTownship,
    selectedDistrict,
    selectedProvince,
    selectedCountry,
  ])

  const isProyecto = selectedBuildingTypeId === buildingTypeIds.proyecto

  // ---------------------------------------------------------------------------
  // Effects
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (hasDefaultAddress) {
      dispatch(
        agregarDireccion({
          ...useSelectorQuoteObject?.QuoteHeader,
          Address_AddressDetail: address?.alias,
          Address_Telephone_Number: phoneData.number,
        })
      )
    }
  }, [hasDefaultAddress])

  useEffect(() => {
    fetchProvinces()
  }, [])

  useEffect(() => {
    if (
      defaultValues?.AddressId !== null ||
      defaultValues?.Address_AddressId !== null
    ) {
      const initLat =
        parseFloat(
          defaultValues?.Latitude || defaultValues?.Address_Latitude
        ) || 8.98
      const initLng =
        parseFloat(
          defaultValues?.Longitude || defaultValues?.Address_Longitude
        ) || -79.51
      setLat(initLat)
      setLng(initLng)
      fetchGenerals(initLat, initLng)
    }
  }, [defaultValues?.AddressId])

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------
  const onLoad = autocom => setAutocomplete(autocom)
  const handleMarker = e => {
    setLat(e.latLng.lat())
    setLng(e.latLng.lng())
    fetchGenerals(e.latLng.lat(), e.latLng.lng())
  }
  const clearError = name => setErrors(prev => ({ ...prev, [name]: false }))
  const setField = (field, value) => {
    clearError(field)
    setAddress(prev => ({ ...prev, [field]: value }))
  }

  const onPlaceChanged = () => {
    if (autocomplete !== null) {
      setLat(autocomplete.getPlace().geometry.location.lat())
      setLng(autocomplete.getPlace().geometry.location.lng())
      fetchGenerals(
        autocomplete.getPlace().geometry.location.lat(),
        autocomplete.getPlace().geometry.location.lng()
      )
    }
  }

  // ---------------------------------------------------------------------------
  // Geographic handlers
  // ---------------------------------------------------------------------------
  const handleCountry = e => {
    setSelectedCountry(e.target.value)
    setCountryCode(e.target.id)
    setShowCountries(false)
    clearError('country')
  }
  const handleProvince = e => {
    setSelectedProvince(e.target.value)
    setProvinceCode(e.target.id)
    setShowProvinces(false)
    fetchDistrict(e.target.id)
    setSelectedDistrict(DISTRICT_PH)
    setSelectedTownship(TOWNSHIP_PH)
    clearError('province')
  }
  const handleDistrict = e => {
    setSelectedDistrict(e.target.value)
    setDistrictCode(e.target.id)
    setShowDistricts(false)
    fetchTownship(e.target.id)
    setSelectedTownship(TOWNSHIP_PH)
    clearError('district')
  }
  const handleTownship = e => {
    setSelectedTownship(e.target.value)
    setTownshipCode(e.target.id)
    setShowTownships(false)
    clearError('township')
  }

  // ---------------------------------------------------------------------------
  // Validation
  // ---------------------------------------------------------------------------
  const validatePhone = () => {
    const number = phoneData.number?.trim()
    const countryCode = phoneData.countryCode || 'PA'
    if (!number) return true
    try {
      const parsed = parsePhoneNumber(number, countryCode)
      if (!parsed?.isValid()) return true
      const numberType = parsed.getType()
      return !numberType || !isValidNumberType(numberType, telephoneTypeId)
    } catch {
      return true
    }
  }

  const handlePhoneChange = data => {
    setPhoneData(data)
    clearError('phoneNumber')
  }

  const handlePhoneBlur = () => {
    if (validatePhone()) {
      setErrors(prev => ({ ...prev, phoneNumber: true }))
    }
  }

  const validate = () => {
    const aliasVal = sanitizeText(address.alias)
    const neighborhoodVal = sanitizeText(address.neighborhood)
    const unitVal = address.unitNumber?.trim()

    const newErrors = {
      country: !countryCode,
      province: !provinceCode,
      district: !districtCode,
      township: !townshipCode,
      neighborhood: !neighborhoodVal,
      unitNumber: !isProyecto && !unitVal,
      alias: !aliasVal || aliasVal.length < 5,
      phoneNumber: validatePhone(),
    }
    setErrors(newErrors)
    return !Object.values(newErrors).some(Boolean)
  }

  // ---------------------------------------------------------------------------
  // Payload builders
  // ---------------------------------------------------------------------------
  const buildPayload = (overrides = {}) => ({
    addressTypeId: '90ACBCF4-BE83-4F03-8B5E-5760FC92B59B',
    countryId: countryCode,
    provinceId: provinceCode,
    districtId: districtCode,
    townshipId: townshipCode,
    buildingTypeId: selectedBuildingTypeId,
    ApartmentOrStoreNumber: address.unitNumber?.trim() || null,
    homeOrFloorNumber: showsFloorAndElevator(selectedBuildingTypeId)
      ? address.floorNumber?.trim() || null
      : null,
    hasElevator: showsFloorAndElevator(selectedBuildingTypeId)
      ? hasElevator
      : false,
    street: sanitizeText(address.neighborhood),
    FinalAddress: computedFinalAddress,
    addressDetail: address.additionalInstructions?.trim() || null,
    name: sanitizeText(address.alias),
    telephoneTypeId,
    phonePrefix: phoneData.Prefix,
    phoneNumber: phoneData.number?.trim() || null,
    TelephoneDefaultMobile: telephoneTypeId === telephoneTypeIds.cellPhone,
    TelephoneDefaultHome: telephoneTypeId === telephoneTypeIds.house,
    TelephoneDefaultWork: telephoneTypeId === telephoneTypeIds.office,
    DefaultShippingAddress: isDefaultDelivery,
    DefaultBillingAddress: isDefaultBilling,
    latitude: lat || null,
    longitude: lng || null,
    ...overrides,
  })

  const buildAddressCotizacion = (data = {}) => ({
    Address_AddressDetail: sanitizeText(address.alias),
    Address_AddressId: data.AddressId || null,
    Address_AddressType: data.AddressTypeId || null,
    Address_AddressTypeId: data.AddressTypeId || null,
    Address_BuildingId: null,
    Address_BuildingName: buildingTypeLabel,
    Address_BuildingType: buildingTypeLabel,
    Address_BuildingTypeId: selectedBuildingTypeId,
    Address_CountryId: countryCode,
    Address_CountryName: selectedCountry,
    Address_DistrictId: districtCode,
    Address_DistrictName: selectedDistrict,
    Address_HomeOrFloorNumber: showsFloorAndElevator(selectedBuildingTypeId)
      ? address.floorNumber || null
      : null,
    Address_Latitude: lat || null,
    Address_Longitude: lng || null,
    Address_NeighborhoodId: null,
    Address_NeighborhoodName: address.neighborhood,
    Address_ProvinceId: provinceCode,
    Address_ProvinceName: selectedProvince,
    Address_Street: computedFinalAddress,
    Address_TownshipId: townshipCode,
    Address_TownshipName: selectedTownship,
    Address_Telephone_Number: phoneData.number || null,
    Address_Name: sanitizeText(address.alias),
  })

  const generateUpdateAddreess = updatedAddress => {
    const addressCotizacion = {
      Address_AddressDetail:
        updatedAddress?.Name || sanitizeText(address.alias),
      Address_AddressId: updatedAddress?.AddressId,
      Address_AddressType: updatedAddress?.AddressTypeId,
      Address_AddressTypeId: null,
      Address_BuildingId: null,
      Address_BuildingName: buildingTypeLabel,
      Address_BuildingType: buildingTypeLabel,
      Address_BuildingTypeId:
        updatedAddress?.BuildingTypeId || selectedBuildingTypeId,
      Address_CountryId: updatedAddress?.CountryId || countryCode,
      Address_CountryName: updatedAddress?.CountryName || selectedCountry,
      Address_DistrictId: updatedAddress?.DistrictId || districtCode,
      Address_DistrictName: updatedAddress?.DistrictName || selectedDistrict,
      Address_HomeOrFloorNumber: updatedAddress?.FloorNumber || null,
      Address_Latitude: lat || null,
      Address_Longitude: lng || null,
      Address_NeighborhoodId: null,
      Address_NeighborhoodName:
        updatedAddress?.NeighborhoodName || address.neighborhood,
      Address_ProvinceId: updatedAddress?.ProvinceId || provinceCode,
      Address_ProvinceName: updatedAddress?.ProvinceName || selectedProvince,
      Address_Street:
        updatedAddress?.Street ||
        updatedAddress?.AddressDetail ||
        computedFinalAddress,
      Address_TownshipId: updatedAddress?.TownshipId || townshipCode,
      Address_TownshipName: updatedAddress?.TownshipName || selectedTownship,
      Address_Telephone_Number: updatedAddress?.PhoneNumber || phoneData.number,
    }
    setDireccion(true)
    setSelectedAddress({ AddressInfo: addressCotizacion })
    dispatch(agregarDireccion(addressCotizacion))

    const savedAddresses = customerSavedLocations?.AddressList || []
    const index = savedAddresses.findIndex(
      addr =>
        addr.AddressInfo?.AddressId?.toLowerCase() ===
          addressCotizacion.Address_AddressId ||
        addr.AddressInfo?.Address_AddressId?.toLowerCase() ===
          addressCotizacion.Address_AddressId
    )
    const updatedAddresses = [...savedAddresses]
    if (index !== -1) {
      updatedAddresses[index] = {
        ...updatedAddresses[index],
        AddressInfo: addressCotizacion,
      }
    } else {
      updatedAddresses.push(addressCotizacion)
    }
    dispatch({
      type: 'SET_CUSTOMER_INFO',
      payload: { AddressList: updatedAddresses },
    })
  }

  // ---------------------------------------------------------------------------
  // Form submit – edit
  // ---------------------------------------------------------------------------
  const handleUpdateAddress = async e => {
    e.preventDefault()
    if (!validate()) return
    let shouldClose = true
    try {
      setFormIsLoading(true)
      setSaveAddressLoadingState(true)
      const payload = {
        AddressId: defaultValues?.AddressId || defaultValues?.Address_AddressId,
        customerId: clienteId || customerId,
        ...buildPayload(),
        TelephoneId:
          defaultValues?.AddressTelephone?.[0]?.AddressPhoneInfo
            ?.AddressTelephoneId,
        TelephoneTypeId: defaultValues?.TelephoneTypeId,
        createdBy: '3fa85f64-5717-4562-b3fc-2c963f66afa6',
        lastModifiedBy: '3fa85f64-5717-4562-b3fc-2c963f66afa6',
      }
      const addressService = !isCountedCustomer
        ? updateAddress(payload)
        : dispatch(
            fetchAddressCountedCustomer({
              payload,
              type: 'update',
            })
          )
      const response = await addressService
      if (!isCountedCustomer && response.data.Status?.Code === 406) {
        setIsErrorAddressModal(true)
        setMessageAddressModal({
          message: response.data.Status?.Message,
          status: 'error',
        })
        setSaveAddressLoadingState(false)
        setFormIsLoading(false)
        shouldClose = false
        return
      }
      await handleMetodoEntrega()
      generateUpdateAddreess(response?.data?.Data || response)
    } catch (error) {
      console.log('Error updating address: ', error)
    } finally {
      setFormIsLoading(false)
      setSaveAddressLoadingState(false)
      if (shouldClose && onClose) onClose()
    }
  }

  // ---------------------------------------------------------------------------
  // Form submit – create
  // ---------------------------------------------------------------------------
  const handleForm = async e => {
    e.preventDefault()
    if (!validate()) return

    const addressCotizacion = buildAddressCotizacion()
    const payload = {
      customerId: clienteId,
      ...buildPayload(),
      createdBy: '3fa85f64-5717-4562-b3fc-2c963f66afa6',
      lastModifiedBy: '3fa85f64-5717-4562-b3fc-2c963f66afa6',
    }
    setFormIsLoading(true)
    setSaveAddressLoadingState(true)

    const addressService = !isCountedCustomer
      ? dispatch(fetchUpdateAddress(payload))
      : dispatch(fetchAddressCountedCustomer({ payload }))
    const Data = await addressService

    if (!isCountedCustomer && Data?.Code === 406) {
      setIsErrorAddressModal(true)
      setMessageAddressModal({ message: Data?.Message, status: 'error' })
      setSaveAddressLoadingState(false)
      setFormIsLoading(false)
      return
    }
    if (Data?.Code === 406) {
      setIsErrorAddressModal(true)
      setMessageAddressModal({ message: Data?.Message, status: 'error' })
      setSaveAddressLoadingState(false)
      setFormIsLoading(false)
      if (!isCountedCustomer) {
        generateUpdateAddreess(Data)
        handleMetodoEntrega()
        setSaveAddressLoadingState(false)
      } else {
        handleMetodoEntrega()
        generateUpdateAddreess(Data)
        dispatch({
          type: 'SET_CUSTOMER_INFO',
          payload: {
            ...customerSavedLocations,
            AddressList: [{ AddressInfo: addressCotizacion }],
          },
        })
        setSaveAddressLoadingState(false)
      }
    } else {
      generateUpdateAddreess(Data)
      await handleMetodoEntrega()
      setSaveAddressLoadingState(false)
      setFormIsLoading(false)
      if (onClose) onClose()
    }
  }

  // ---------------------------------------------------------------------------
  // Location endpoints
  // ---------------------------------------------------------------------------
  const fetchGenerals = async (latitud, longitude) => {
    setFormIsLoading(true)
    const payload = {
      Latitude: latitud,
      Longitude: longitude,
    }
    const Data = await dispatch(fetchAddressByGeolocation(payload))
    if (Data && Data?.length > 0) {
      const loc = Data[0]
      setSelectedCountry(loc.Country)
      setCountryCode(loc.CountryId)
      setSelectedProvince(loc.Province)
      setProvinceCode(loc.ProvinceId)
      setSelectedDistrict(loc.District)
      setDistrictCode(loc.DistrictId)
      fetchDistrict(loc.ProvinceId)
      setSelectedTownship(loc.Township)
      setTownshipCode(loc.TownshipId)
      fetchTownship(loc.DistrictId)
      setErrors(prev => ({
        ...prev,
        country: false,
        province: false,
        district: false,
        township: false,
      }))
    } else {
      console.error('Error getting Generals: ', Data)
    }
    setFormIsLoading(false)
  }

  const fetchProvinces = async () => {
    const Data = await dispatch(
      fetchProvincesByCountryAction(
        districtCode === '' ? defaultCountryId.countryId : districtCode
      )
    )
    if (Data && Data?.length > 0) setProvinces(Data)
    else console.error('Error getting Provinces: ', Data)
  }

  const fetchDistrict = async provinceCode => {
    const Data = await dispatch(fetchDistrictByProvince(provinceCode))
    if (Data && Data?.length > 0) {
      setDistricts(Data)
    } else {
      console.error('Error getting districts: ', Data)
    }
  }

  const fetchTownship = async districtCode => {
    const Data = await dispatch(fetchTownshipByDistrict(districtCode))
    if (Data && Data?.length > 0) {
      setTownships(Data)
    } else {
      console.error('Error getting TownShips: ', Data)
    }
  }

  // ---------------------------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------------------------
  return (
    <div
      className={
        isModalScreen
          ? 'content-form-direccion-modal'
          : 'content-form-direccion'
      }>
      {showHeader && !hasDefaultAddress && isModalScreen ? (
        <label className="add-direction-title-label-modal">
          {t('ADDRESS_FORM_T.ADD_NEW_DIRECTION')}
        </label>
      ) : null}
      {showHeader && hasDefaultAddress && (
        <div className="address-edition-header">
          <img
            src={no}
            alt="close-icon"
            className="close-icon-dispach-menu"
            onClick={onClose}
          />
        </div>
      )}

      <div
        className={
          isModalScreen ? 'content-form-direccion-modal-padding' : null
        }>
        {/* Google Autocomplete */}
        <Autocomplete onLoad={onLoad} onPlaceChanged={onPlaceChanged}>
          <input
            type="text"
            placeholder={t('ADDRESS_FORM_T.SEARCH_PLACEHOLDER')}
            style={{
              boxSizing: 'border-box',
              width: '100%',
              padding: '10px 25px',
              borderRadius: '6px',
              border: '1px solid #C8C8C8',
              fontSize: '14px',
              outline: 'none',
              marginBottom: '5px',
              textOverflow: 'ellipses',
            }}
          />
        </Autocomplete>

        {/* Map */}
        <GoogleMap
          id="search-map-build"
          mapContainerStyle={mapContainerStyle}
          zoom={15}
          onClick={handleMarker}
          center={{ lat, lng }}>
          <Marker position={{ lat, lng }} />
        </GoogleMap>

        {!formIsLoading ? (
          <form onSubmit={hasDefaultAddress ? handleUpdateAddress : handleForm}>
            <p className="comment">{t('ADDRESS_FORM_T.COMPLETE_FORM_HINT')}</p>

            {/* País / Provincia */}
            <div className="form-group-select">
              <div>
                <div className="address-search-filter">
                  <button
                    type="button"
                    className="address-filter-content"
                    disabled
                    onClick={() => setShowCountries(!showCountries)}>
                    <h2>{`${selectedCountry}*`}</h2>
                    <FontAwesomeIcon icon={faChevronDown} />
                  </button>
                  <ul
                    className={
                      showCountries
                        ? 'address-filter-options'
                        : 'address-filter-options-hidden'
                    }>
                    <li className="address-filter-option">
                      <input
                        type="radio"
                        className="radio"
                        id="2d3d33c2-3401-40a1-858b-ae0140b0d376"
                        name="pais"
                        value="PANAMA"
                        onClick={handleCountry}
                        disabled
                      />
                      <label htmlFor="2d3d33c2-3401-40a1-858b-ae0140b0d376">
                        {t('ADDRESS_FORM_T.COUNTRY_OPTION_PANAMA')}
                      </label>
                    </li>
                  </ul>
                </div>
                {errors.country && (
                  <p className={styles.errorMessage}>
                    {t('ADDRESS_FORM_T.ERROR_SELECT_COUNTRY')}
                  </p>
                )}
              </div>

              <div>
                <div className="address-search-filter">
                  <button
                    type="button"
                    className="address-filter-content"
                    disabled
                    onClick={() => setShowProvinces(!showProvinces)}>
                    <h2>{`${selectedProvince}*`}</h2>
                    <FontAwesomeIcon icon={faChevronDown} />
                  </button>
                  <ul
                    className={
                      showProvinces
                        ? 'address-filter-options'
                        : 'address-filter-options-hidden'
                    }>
                    {provinces.map(province => (
                      <li
                        className="address-filter-option"
                        key={province.ProvinceId}>
                        <input
                          type="radio"
                          className="radio"
                          id={province.ProvinceId}
                          name="provincia"
                          value={province.Name}
                          onClick={handleProvince}
                          disabled={hasDefaultAddress}
                        />
                        <label htmlFor={province.ProvinceId}>
                          {province.Name}
                        </label>
                      </li>
                    ))}
                  </ul>
                </div>
                {errors.province && (
                  <p className={styles.errorMessage}>
                    {t('ADDRESS_FORM_T.ERROR_SELECT_PROVINCE')}
                  </p>
                )}
              </div>
            </div>

            {/* Distrito / Corregimiento */}
            <div className="form-group-select">
              <div>
                <div className="address-search-filter">
                  <button
                    type="button"
                    className="address-filter-content"
                    disabled
                    onClick={() => setShowDistricts(!showDistricts)}>
                    <h2>{`${selectedDistrict}*`}</h2>
                    <FontAwesomeIcon icon={faChevronDown} />
                  </button>
                  <ul
                    className={
                      showDistricts
                        ? 'address-filter-options'
                        : 'address-filter-options-hidden'
                    }>
                    {districts.map(district => (
                      <li
                        className="address-filter-option"
                        key={district.DistrictId}>
                        <input
                          type="radio"
                          className="radio"
                          id={district.DistrictId}
                          name="distrito"
                          value={district.Name}
                          onClick={handleDistrict}
                          disabled={hasDefaultAddress}
                        />
                        <label htmlFor={district.DistrictId}>
                          {district.Name}
                        </label>
                      </li>
                    ))}
                  </ul>
                </div>
                {errors.district && (
                  <p className={styles.errorMessage}>
                    {t('ADDRESS_FORM_T.ERROR_SELECT_DISTRICT')}
                  </p>
                )}
              </div>

              <div>
                <div className="address-search-filter">
                  <button
                    type="button"
                    className="address-filter-content"
                    disabled
                    onClick={() => setShowTownships(!showTownships)}>
                    <h2>{`${selectedTownship}*`}</h2>
                    <FontAwesomeIcon icon={faChevronDown} />
                  </button>
                  <ul
                    className={
                      showTownships
                        ? 'address-filter-options'
                        : 'address-filter-options-hidden'
                    }>
                    {townships.map(township => (
                      <li
                        className="address-filter-option"
                        key={township.TownshipId}>
                        <input
                          type="radio"
                          className="radio"
                          id={township.TownshipId}
                          name="corregimiento"
                          value={township.Name}
                          onClick={handleTownship}
                          disabled={hasDefaultAddress}
                        />
                        <label htmlFor={township.TownshipId}>
                          {township.Name}
                        </label>
                      </li>
                    ))}
                  </ul>
                </div>
                {errors.township && (
                  <p className={styles.errorMessage}>
                    {t('ADDRESS_FORM_T.ERROR_SELECT_TOWNSHIP')}
                  </p>
                )}
              </div>
            </div>

            {/* Tipo de Vivienda */}
            <div className="address-section">
              <h3 className="address-section-title">
                {t('ADDRESS_FORM_T.BUILDING_TYPE_TITLE')}
              </h3>
              <BuildingTypeSelector
                types={getBuildingTypes()}
                selectedId={selectedBuildingTypeId}
                onSelect={setSelectedBuildingTypeId}
              />
            </div>

            {/* Número de unidad + Vecindario */}
            <div className="form-group-select">
              {!isProyecto && (
                <div className="edificio-input">
                  <label className="address-input-label">
                    {t(
                      UNIT_NUMBER_KEYS[selectedBuildingTypeId] ||
                        'ADDRESS_FORM_T.UNIT_NUMBER_UNIDAD'
                    )}
                    <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder={t(
                      UNIT_NUMBER_PLACEHOLDER_KEYS[selectedBuildingTypeId] ||
                        'ADDRESS_FORM_T.PLACEHOLDER_UNIT_NUMBER_UNIDAD'
                    )}
                    maxLength={10}
                    value={address.unitNumber}
                    onChange={e =>
                      setField('unitNumber', e.target.value.replace(/^\s+/, ''))
                    }
                  />
                  <p className={styles.errorMessage}>
                    {errors.unitNumber
                      ? t('ADDRESS_FORM_T.ERROR_UNIT_NUMBER')
                      : ''}
                  </p>
                </div>
              )}
              <div className="edificio-input">
                <label className="address-input-label">
                  {t('ADDRESS_FORM_T.LABEL_NEIGHBORHOOD')}
                  <span className="required">*</span>
                </label>
                <input
                  type="text"
                  placeholder={t('ADDRESS_FORM_T.PLACEHOLDER_NEIGHBORHOOD')}
                  maxLength={50}
                  value={address.neighborhood}
                  onChange={e =>
                    setField('neighborhood', e.target.value.replace(/^\s+/, ''))
                  }
                />
                <div className={styles.inputFooter}>
                  <p className={styles.errorMessage}>
                    {errors.neighborhood
                      ? t('ADDRESS_FORM_T.ERROR_NEIGHBORHOOD')
                      : ''}
                  </p>
                  <p className="char-counter" style={{ marginTop: 0 }}>
                    {t('ADDRESS_FORM_T.CHARS_REMAINING', {
                      count: 50 - address.neighborhood.length,
                    })}
                  </p>
                </div>
              </div>
            </div>

            {/* Piso + Elevador */}
            {showsFloorAndElevator(selectedBuildingTypeId) && (
              <>
                <div className="edificio-input" style={{ marginTop: '12px' }}>
                  <label className="address-input-label">
                    {t('ADDRESS_FORM_T.LABEL_FLOOR')}
                  </label>
                  <input
                    type="text"
                    placeholder={t('ADDRESS_FORM_T.PLACEHOLDER_FLOOR')}
                    maxLength={10}
                    value={address.floorNumber}
                    onChange={e =>
                      setField(
                        'floorNumber',
                        e.target.value.replace(/^\s+/, '')
                      )
                    }
                  />
                </div>
                <div className="elevator-toggle-row">
                  <span className="elevator-label">
                    {t('ADDRESS_FORM_T.ELEVATOR_LABEL')}
                  </span>
                  <Switch
                    checked={hasElevator}
                    onChange={e => setHasElevator(e.target.checked)}
                    size="small"
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#003082',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track':
                        { backgroundColor: '#003082' },
                    }}
                  />
                </div>
              </>
            )}

            {/* Contacto & Alias */}
            <div className="address-section" style={{ marginTop: '20px' }}>
              <h3 className="address-section-title">
                {t('ADDRESS_FORM_T.CONTACT_SECTION_TITLE')}
              </h3>

              <PhoneTypeSelector
                types={getPhoneTypes()}
                selectedId={telephoneTypeId}
                onSelect={setTelephoneTypeId}
              />

              <div className="form-group-select" style={{ marginTop: '12px' }}>
                <div className="edificio-input">
                  <label className="address-input-label">
                    {t('ADDRESS_FORM_T.LABEL_PHONE')}
                    <span className="required">*</span>
                  </label>
                  <PhoneNumberField
                    value={phoneData}
                    onChange={handlePhoneChange}
                    onBlur={handlePhoneBlur}
                  />
                  <p className={styles.errorMessage}>
                    {errors.phoneNumber ? t('ADDRESS_FORM_T.ERROR_PHONE') : ''}
                  </p>
                </div>

                <div className="edificio-input">
                  <label className="address-input-label">
                    {t('ADDRESS_FORM_T.LABEL_ALIAS')}
                    <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder={t('ADDRESS_FORM_T.PLACEHOLDER_ALIAS')}
                    maxLength={30}
                    minLength={5}
                    value={address.alias}
                    onChange={e =>
                      setField('alias', e.target.value.replace(/^\s+/, ''))
                    }
                  />
                  <div className={styles.inputFooter}>
                    <p className={styles.errorMessage}>
                      {errors.alias ? t('ADDRESS_FORM_T.ERROR_ALIAS') : ''}
                    </p>
                    <p className="char-counter" style={{ marginTop: 0 }}>
                      {t('ADDRESS_FORM_T.CHARS_REMAINING', {
                        count: 30 - address.alias.length,
                      })}
                    </p>
                  </div>
                </div>
              </div>

              {/* Instrucciones adicionales */}
              <div className="edificio-input" style={{ marginTop: '12px' }}>
                <textarea
                  className="additional-instructions-input"
                  placeholder={t(
                    'ADDRESS_FORM_T.ADDITIONAL_INSTRUCTIONS_PLACEHOLDER'
                  )}
                  maxLength={255}
                  rows={3}
                  value={address.additionalInstructions}
                  onChange={e =>
                    setField('additionalInstructions', e.target.value)
                  }
                />
                <p className="char-counter">
                  {address.additionalInstructions.length}/255
                </p>
              </div>
            </div>

            {/* Dirección por defecto */}
            {!isCountedCustomer && (
              <DefaultAddressSection
                isDefaultDelivery={isDefaultDelivery}
                onDeliveryChange={setIsDefaultDelivery}
                isDefaultBilling={isDefaultBilling}
                onBillingChange={setIsDefaultBilling}
              />
            )}

            {/* Action buttons */}
            {hasDefaultAddress && (
              <div className="button-save-edition-type">
                <button
                  type="submit"
                  disabled={saveAddressLoadingState}
                  className="edition-button-save">
                  {saveAddressLoadingState ? (
                    <CircularProgress size={20} />
                  ) : (
                    t('ADDRESS_FORM_T.BTN_SAVE_EDIT')
                  )}
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  disabled={saveAddressLoadingState}
                  className="edition-button-close">
                  {t('ADDRESS_FORM_T.BTN_CLOSE')}
                </button>
              </div>
            )}
            {!hasDefaultAddress && (
              <div className="metodo-entrega-button-save">
                <button type="submit" className="entrega-buttons-save">
                  {saveAddressLoadingState ? (
                    <CircularProgress size={20} />
                  ) : isModalScreen ? (
                    t('ADDRESS_FORM_T.BTN_SAVE')
                  ) : (
                    t('ADDRESS_FORM_T.BTN_CONTINUE')
                  )}
                </button>
              </div>
            )}
          </form>
        ) : (
          <MiniLoading />
        )}
      </div>
    </div>
  )
}

export default Mapa
